import { isRouteAllowed } from '../utils'
import { Navigate, useLocation, Outlet } from 'react-router-dom'
import { useUserContext } from '../context/UserContext'
import Spinner from '../components/Spinner/Spinner.jsx'

const allowedForManufacturers = [
  '/admin-dashboard',
  '/saddle-admin',
  '/saddles/manage/:encodedId?',
  '/banner',
  '/onboarding',
  '/shopify-connect',
  '/shopify/processing',
  '/shopify/callback',
  '/stripe-connect',
  '/profile',
  '/orders',
  '/custom-saddle-orders',
  '/team-members',
  '/file-browser'
]

// Private viewers: accounts invited via File Browser "Manage access" — they may
// only see the dashboard shell and the private folders shared with them.
const allowedForViewers = ['/admin-dashboard', '/profile', '/file-browser']

const allowedForSuperAdmins = [
  '/admin-dashboard',
  '/manufacturers',
  '/manufacturers/create',
  '/manufacturers/:id',
  '/horse-admin',
  '/horse-admin/horse/edit/:horseId',
  '/saddle-admin',
  '/saddles/manage/:encodedId?',
  '/all-queries',
  '/event-management',
  '/banner-section',
  '/orders',
  '/order',
  '/custom-saddle-orders',
  '/booking-management',
  '/settings',
  '/file-browser'
]

export const ProtectedRouteGuard = () => {
  const { userLoading, userData } = useUserContext()
  const isSignedIn = Boolean(localStorage.getItem('isSignedIn'))
  const { pathname } = useLocation()

  if (!isSignedIn && pathname !== '/') {
    return <Navigate to="/" replace />
  }

  if (isSignedIn && (userLoading || !userData)) {
    return <Spinner />
  }

  const isSimpleAdmin =
    userData?.brand !== 'saddlefitadmin' && userData?.type === 'Admin'
  const isTeamMember =
    userData?.brand !== 'saddlefitadmin' && userData?.type === 'Member'
  const isSaddlefitAdmin = userData?.brand === 'saddlefitadmin' && userData?.type === 'Admin'
  const isPrivateViewer = userData?.brand === 'saddlefitadmin' && userData?.type === 'Member'
  const simpleFallback = '/admin-dashboard'
  const superAdminFallback = '/admin-dashboard'

  const isShopifyFlow = pathname.startsWith('/shopify/')

  if (isPrivateViewer && !isRouteAllowed(pathname, allowedForViewers)) {
    return <Navigate to="/file-browser" replace />
  }

  if (isSaddlefitAdmin && !isRouteAllowed(pathname, allowedForSuperAdmins) && !pathname.startsWith('/orders') && !pathname.startsWith('/order')) {
    return <Navigate to={superAdminFallback} replace />
  }

  const forbiddenForMembers = ['/team-members', '/onboarding', '/shopify-connect', '/stripe-connect']
  if (isTeamMember && forbiddenForMembers.includes(pathname)) {
    return <Navigate to={simpleFallback} replace />
  }

  if (
    !isShopifyFlow &&
    (isSimpleAdmin || isTeamMember) &&
    pathname !== simpleFallback &&
    !isRouteAllowed(pathname, allowedForManufacturers) &&
    !pathname.startsWith('/orders') &&
    !pathname.startsWith('/order')
  ) {
    return <Navigate to={simpleFallback} replace />
  }

  return <Outlet />
}

export const AuthRouteGuard = ({ element: Element, ...rest }) => {
  const { userLoading, userData } = useUserContext()
  const isSignedIn = Boolean(localStorage.getItem('isSignedIn'))

  if (userLoading || (isSignedIn && !userData)) return <Spinner />

  if (isSignedIn) {
    if (window.location.pathname === '/profile') {
      return <Element {...rest} />
    }
    return <Navigate to="/admin-dashboard" replace />
  }

  return <Element {...rest} />
}
